import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Text,
  Flex,
  Spinner,
  useDisclosure,
} from '@chakra-ui/react';
import { 
  getShopifyProducts, 
  getShopifyOrderInfo,
} from '../../../../services/flaskService';
import { ShopifyProduct, ShopifyOrderInfo } from '../../../../types/shopify';
import { SHOPIFY_DATA_UNAVAILABLE_MESSAGE } from '../../../../utils/shopifyUtils';
import { ProductCard } from './ProductCard';
import { ProductModal } from './ProductModal';
import { ff } from '../../../../theme';

interface ShopifyProductDisplayProps {
  clientId: string;
  productVariants: Record<string, string[]>;
  orderId?: string;
}

const ShopifyProductDisplay: React.FC<ShopifyProductDisplayProps> = ({
  clientId,
  orderId,
  productVariants,
}) => {
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [orderInfo, setOrderInfo] = useState<ShopifyOrderInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<ShopifyProduct | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        if (orderId) {
          // Show what this order actually contains, not the whole campaign catalogue.
          const fetchedOrderInfo = await getShopifyOrderInfo(clientId, orderId);
          if (!fetchedOrderInfo.success) {
            setError(SHOPIFY_DATA_UNAVAILABLE_MESSAGE);
            return;
          }
          // lineItem.id is the Shopify product id; dedupe so one product ordered
          // in two variants renders a single card (matching the existing find()).
          const orderedProductIds = Array.from(
            new Set(fetchedOrderInfo.order?.lineItems.map(item => item.id) ?? [])
          );
          setProducts(
            orderedProductIds.length > 0
              ? await getShopifyProducts(clientId, orderedProductIds)
              : []
          );
          setOrderInfo(fetchedOrderInfo);
        } else {
          setProducts(await getShopifyProducts(clientId, Object.keys(productVariants)));
        }

        setError(null);
      } catch (err) {
        setError(SHOPIFY_DATA_UNAVAILABLE_MESSAGE);
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    if (Object.keys(productVariants).length > 0) {
      fetchData();
    }
  }, [clientId, productVariants, orderId]);

  if (loading) {
    return (
      <Flex justify="center" align="center" p={2}>
        <Spinner color={ff.ink} />
      </Flex>
    );
  }

  if (error) {
    return (
      <Box p={2} color={ff.text2} textAlign="center" fontSize="14px">
        {error}
      </Box>
    );
  }

  if (products.length === 0) {
    return (
      <Box p={2} color={ff.text2} textAlign="center" fontSize="14px">
        No products found
      </Box>
    );
  }

  return (
    <>
      <Grid
        templateColumns={{ base: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' }}
        gap={3}
      >
        {products.map((product) => {
          const selectedVariant = orderId && orderInfo?.order?.lineItems.find(item => item.id === product.id)?.variant || null;
          return (
            <ProductCard
              key={product.id}
              product={product}
              selectedVariant={selectedVariant}
              showVariant={!!orderId}
              onProductClick={() => {
                setSelectedProduct(product);
                onOpen();
              }}
              availableVariantIds={productVariants[product.id]}
            />
          );
        })}
      </Grid>
      {selectedProduct && (
        <ProductModal
          isOpen={isOpen}
          onClose={onClose}
          product={selectedProduct}
          selectedVariant={orderId && orderInfo?.order?.lineItems.find(item => item.id === selectedProduct.id)?.variant || null}
          productVariants={productVariants[selectedProduct.id]}
        />
      )}
    </>
  );
};

export default ShopifyProductDisplay;